import React from 'react';
import { Database, CheckCircle, Loader2, Server, Shield, Tag, Building2, MapPin, User, Package, Factory, AlertTriangle, Info, Cpu, Globe, Layers } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * AssetSelector Component
 * Allows users to select support assets for CTI analysis
 * Shows detailed information about each asset including type, description, and criticality
 * 
 * @param {Object} props
 * @param {Array} props.assets - Array of asset objects with selection state
 * @param {Function} props.onToggle - Callback when an asset selection is toggled
 * @param {boolean} props.isLoading - Loading state
 */
const AssetSelector = ({ assets, onToggle, isLoading }) => {
    const { t } = useTranslation();

    if (isLoading) return (
        <div className="text-center py-8">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-indigo-500" />
            <p className="text-gray-500 mt-2">{t('workshop4.assetSelector.loading', 'Chargement des actifs...')}</p>
        </div>
    );

    if (assets.length === 0) return (
        <div className="text-center py-12">
            <Database className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">{t('workshop4.assetSelector.noSupportAssets', 'Aucun actif support trouvé')}</p>
            <p className="text-sm text-gray-400 mt-2">{t('workshop4.assetSelector.noSupportAssetsHint', 'Vérifiez que les valeurs métier ont des actifs support associés dans l\'Atelier 1')}</p>
        </div>
    );

    const selectedCount = assets.filter(a => a.selected).length;
    const analyzedCount = assets.filter(a => a.wasAnalyzed).length;

    // Get criticality badge color
    const getCriticalityColor = (criticality) => {
        switch (criticality?.toLowerCase()) {
            case 'critical':
            case 'critique':
                return 'bg-red-100 text-red-800 border-red-200';
            case 'high':
            case 'élevée':
            case 'élevé':
                return 'bg-orange-100 text-orange-800 border-orange-200';
            case 'medium':
            case 'moyenne':
            case 'moyen':
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'low':
            case 'faible':
                return 'bg-green-100 text-green-800 border-green-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    // Get asset type icon based on type/nature
    const getAssetTypeIcon = (asset) => {
        const type = (asset.type || asset.nature || '').toLowerCase();
        if (type.includes('server') || type.includes('serveur')) {
            return <Server className="h-5 w-5 text-blue-500" />;
        } else if (type.includes('security') || type.includes('sécurité')) {
            return <Shield className="h-5 w-5 text-green-500" />;
        } else if (type.includes('network') || type.includes('réseau')) {
            return <Globe className="h-5 w-5 text-purple-500" />;
        } else if (type.includes('logiciel') || type.includes('software') || type.includes('application')) {
            return <Cpu className="h-5 w-5 text-indigo-500" />;
        } else if (type.includes('equipment') || type.includes('équipement')) {
            return <Layers className="h-5 w-5 text-orange-500" />;
        } else if (type.includes('locaux') || type.includes('building') || type.includes('premises')) {
            return <Building2 className="h-5 w-5 text-amber-500" />;
        } else {
            return <Database className="h-5 w-5 text-gray-500" />;
        }
    };

    return (
        <div className="space-y-6">
            {/* Header with Selection Controls */}
            <div className="flex items-center justify-between bg-gradient-to-r from-gray-50 to-indigo-50 p-4 rounded-xl border border-gray-100">
                <div>
                    <p className="text-gray-700 font-medium">
                        {t('workshop4.assetSelector.selectAssets', 'Sélectionnez les actifs à analyser')}
                    </p>
                    <div className="flex items-center gap-4 mt-1">
                        <p className="text-sm text-gray-500">
                            {t('workshop4.assetSelector.selectedCount', '{{count}} actif(s) sélectionné(s) sur {{total}}', { count: selectedCount, total: assets.length })}
                        </p>
                        {analyzedCount > 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                {analyzedCount} {t('workshop4.assetSelector.alreadyAnalyzed', 'déjà analysé(s)')}
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={() => assets.forEach(asset => !asset.selected && onToggle(asset.id))}
                        className="text-sm bg-indigo-100 text-indigo-700 hover:bg-indigo-200 px-3 py-1.5 rounded-lg font-medium transition-colors"
                    >
                        {t('workshop4.assetSelector.selectAll', 'Tout sélectionner')}
                    </button>
                    <button
                        onClick={() => assets.forEach(asset => asset.selected && onToggle(asset.id))}
                        className="text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 px-3 py-1.5 rounded-lg font-medium transition-colors"
                    >
                        {t('workshop4.assetSelector.deselectAll', 'Tout désélectionner')}
                    </button>
                </div>
            </div>

            {/* Assets Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {assets.map(asset => (
                    <div
                        key={asset.id}
                        onClick={() => onToggle(asset.id)}
                        className={`relative p-5 rounded-xl border-2 cursor-pointer transition-all duration-200 hover:shadow-lg ${asset.selected
                            ? 'border-indigo-400 bg-indigo-50/50 shadow-md'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                    >
                        {/* Previously Analyzed Badge */}
                        {asset.wasAnalyzed && (
                            <div className="absolute -top-2 left-4 bg-green-600 text-white rounded-full px-2.5 py-0.5 text-xs font-bold shadow-sm z-10">
                                {t('workshop4.assetSelector.previouslyAnalyzed', 'Déjà analysé')}
                            </div>
                        )}

                        {/* Selection Indicator */}
                        <div className={`absolute top-3 right-3 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${asset.selected
                            ? 'bg-indigo-500 border-indigo-500'
                            : 'border-gray-300 hover:border-indigo-400'
                            }`}>
                            {asset.selected && <CheckCircle className="h-4 w-4 text-white" />}
                        </div>

                        <div className="flex items-start space-x-4">
                            {/* Asset Type Icon */}
                            <div className={`p-3 rounded-lg shrink-0 ${asset.selected ? 'bg-indigo-100' : 'bg-gray-100'}`}>
                                {getAssetTypeIcon(asset)}
                            </div>

                            {/* Asset Info */}
                            <div className="flex-1 min-w-0 pr-8">
                                <h4 className="font-semibold text-gray-800 text-lg">
                                    {asset.name}
                                </h4>

                                {/* Asset Type/Nature */}
                                <div className="flex items-center mt-1 text-sm text-gray-500">
                                    <Tag className="h-3.5 w-3.5 mr-1.5 shrink-0" />
                                    <span>{asset.type || asset.nature || t('workshop4.assetSelector.unknownType', 'Type non défini')}</span>
                                </div>

                                {/* Description */}
                                {asset.description && (
                                    <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                                        {asset.description}
                                    </p>
                                )}

                                {/* Technical Details Section */}
                                <div className="mt-3 p-3 bg-gray-50 rounded-lg space-y-2">
                                    {/* Vendor & Product Row */}
                                    {(asset.vendor || asset.product) && (
                                        <div className="flex flex-wrap items-center gap-2">
                                            {asset.vendor && (
                                                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                                                    <Factory className="h-3 w-3 mr-1.5" />
                                                    {asset.vendor}
                                                </span>
                                            )}
                                            {asset.product && (
                                                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                                                    <Package className="h-3 w-3 mr-1.5" />
                                                    {asset.product}
                                                    {asset.version && <span className="ml-1 opacity-75">v{asset.version}</span>}
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    {/* Owner & Location Row */}
                                    {(asset.owner || asset.location) && (
                                        <div className="flex flex-wrap items-center gap-2">
                                            {asset.owner && (
                                                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                                                    <User className="h-3 w-3 mr-1.5" />
                                                    {asset.owner}
                                                </span>
                                            )}
                                            {asset.location && (
                                                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                                                    <MapPin className="h-3 w-3 mr-1.5" />
                                                    {asset.location}
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    {/* Criticality Badge */}
                                    {asset.criticality && (
                                        <div className="pt-1">
                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${getCriticalityColor(asset.criticality)}`}>
                                                <AlertTriangle className="h-3 w-3 mr-1.5" />
                                                {t('workshop4.assetSelector.criticality', 'Criticité')}: {asset.criticality}
                                            </span>
                                        </div>
                                    )}

                                    {/* Show placeholder if no technical details */}
                                    {!asset.vendor && !asset.product && !asset.owner && !asset.location && !asset.criticality && (
                                        <p className="text-xs text-gray-400 italic">
                                            {t('workshop4.assetSelector.noTechnicalInfo', 'Aucune information technique disponible')}
                                        </p>
                                    )}
                                </div>

                                {/* Technical Keywords if available */}
                                {asset.technicalKeywords && (
                                    <div className="mt-3">
                                        <p className="text-xs text-gray-500">
                                            <span className="font-medium text-gray-600">{t('workshop4.assetSelector.keywords', 'Mots-clés')}:</span>{' '}
                                            <span className="italic">{asset.technicalKeywords}</span>
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Selected Status */}
                        {asset.selected && (
                            <div className="mt-4 pt-3 border-t border-indigo-200">
                                <p className="text-xs text-indigo-600 font-medium flex items-center">
                                    <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                                    {t('workshop4.assetSelector.willBeAnalyzed', 'Cet actif sera analysé pour les vulnérabilités et techniques d\'attaque')}
                                </p>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Info Message */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-start">
                    <Info className="h-5 w-5 text-blue-600 mr-3 mt-0.5 shrink-0" />
                    <div>
                        <p className="text-sm text-blue-800 font-medium">
                            {t('workshop4.assetSelector.infoTitle', 'Information sur l\'analyse CTI')}
                        </p>
                        <p className="text-sm text-blue-700 mt-1">
                            {t('workshop4.assetSelector.infoDescription', 'L\'analyse recherchera les vulnérabilités connues (CVE) et les techniques d\'attaque MITRE ATT&CK associées à chaque actif sélectionné.')}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AssetSelector;